import { useForm, useWatch } from "react-hook-form";

import type { CommunityGroupV2Dto, ConstraintListItemDto, UserRoleCommunityGroupPermissionsDto } from "@/api/types";
import bgPlaceholder from "@/assets/images/image-placeholder.png";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormGroupIconPicker } from "@/components/Form/FormGroupIconPicker";
import { FormImageInput } from "@/components/Form/FormImageInput";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { FormTextArea } from "@/components/Form/FormTextArea";
import type { GroupIconType } from "@/components/GroupIcon/GroupIcon";
import { Translate01Icon } from "@/components/Icons/Translate01Icon";
import type { FormImage } from "@/components/ImageInput/useImageInput";
import { WithAppPreviewLayout } from "@/components/Layouts/WithAppPreviewLayout";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PermissionSelector } from "@/components/PermissionSelector/PermissionSelector";
import { Phone } from "@/components/Phone/Phone";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Notice } from "@/design-system/Notice";
import { getFlagIcon } from "@/helpers/flags";
import type { FormPermissions } from "@/helpers/permissions";
import { createImageSizeRule, createImageTypeRule, createRequiredStringRule } from "@/helpers/rules";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { useKey } from "@/hooks/useKey";
import { useSlug } from "@/hooks/useSlug";
import { AppGroupDetailsView } from "@/modules/community-groups/components/AppGroupDetailsView";
import { routes } from "@/routes";
import type { Languages, TranslationKey } from "@/translations";
import { useTranslation } from "@/translations";

import { GroupAudienceSelector } from "../GroupAudienceSelector";

interface CreateOrEditTranslations {
  title: string;
  actionFinish: string;
  fieldName: TranslationKey;
  fieldNamePlaceholder: string;
  fieldDescription: TranslationKey;
  fieldDescriptionPlaceholder: string;
  fieldIcon: string;
  fieldIconDescription: string;
  fieldIconPlaceholder: string;
  fieldImage: string;
  fieldImageDescription: string;
  fieldImagePlaceholder: string;
  fieldAddressDescription: string;
  fieldBuildingDescription: string;
  fieldCompanyDescription: string;
  fieldFloorDescription: string;
  fieldAudienceDescription?: string;
  appPreviewTitle: string;
  appPreviewOpen: string;
  canComment: string;
  canCreateAnnouncement: string;
  canCreatePost: string;
  canListPosts: string;
}

export interface CommunityGroupFormProps {
  type: CommunityGroupV2Dto["type"];
  contentLanguageId: Languages | undefined;
  canToggleStatus: boolean;
  isMultiProjectGroup: boolean;
  multiProjectGroupType: "system" | "connection" | undefined;
  isEditMode: boolean;
  defaultValues: CreateOrEditCommunityGroupFormValues;
  isSubmitting: boolean;
  translations: CreateOrEditTranslations;
  onSubmit: (data: CreateOrEditCommunityGroupFormValues) => Promise<void>;
}

export function CommunityGroupForm({
  contentLanguageId,
  type,
  isMultiProjectGroup,
  multiProjectGroupType,
  translations,
  defaultValues,
  isSubmitting,
  ...props
}: CommunityGroupFormProps): React.ReactNode {
  const [appPreviewOpen, appPreviewOpenHandlers] = useBool();
  const { t } = useTranslation();
  const user = useSessionUser();
  const slug = useSlug();
  const formMethods = useForm<CreateOrEditCommunityGroupFormValues>({
    mode: "onChange",
    defaultValues: defaultValues,
  });
  const sessionUser = useSessionUser();

  useKey("Escape", appPreviewOpenHandlers.setFalse, appPreviewOpen);

  const onSave = ({
    audience,
    isRealEstateGroup,
    isAllResidentsAdded,
  }: {
    audience: ConstraintListItemDto[];
    isRealEstateGroup: boolean;
    isAllResidentsAdded: boolean;
  }) => {
    formMethods.setValue("audience", audience);
    formMethods.setValue("isRealEstate", isRealEstateGroup);
    formMethods.setValue("isAllResidentsAdded", isAllResidentsAdded);
  };
  const flagIcon = getFlagIcon({
    languageId: contentLanguageId || sessionUser.uiLanguage.id,
    width: 16,
  });

  const permissions = useWatch({
    control: formMethods.control,
    name: "permissions",
  });

  const permissionFields = [
    { key: "canCommentPost", label: translations.canComment },
    { key: "canCreateAnnouncement", label: translations.canCreateAnnouncement },
    { key: "canCreatePost", label: translations.canCreatePost },
    { key: "canListPosts", label: translations.canListPosts },
  ];

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={translations.title}
      breadcrumbs={
        <Breadcrumbs
          pages={[
            {
              name: t("page.interest-group.list.title"),
              to: routes.interestGroups.list({
                slug,
              }),
            },
            {
              name: translations.title,
            },
          ]}
        />
      }
    >
      <Form.Root formMethods={formMethods} onSubmit={props.onSubmit}>
        {isMultiProjectGroup && (
          <Form.Container>
            <Form.Body>
              <FormField label={t("page.community-groups.create-or-edit.posting-level.status")}>
                <FormCheckbox<CreateOrEditCommunityGroupFormValues>
                  name="enabled"
                  label={t("page.community-groups.create-or-edit.posting-level.status.label")}
                  disabled={!props.canToggleStatus}
                />
              </FormField>
              <FormField
                label={t("page.platform-groups.form.posting-level.label")}
                tooltip={t("page.platform-groups.form.posting-level.tooltip")}
              >
                <FormSelect<CreateOrEditCommunityGroupFormValues, string>
                  name="postingLevel"
                  items={
                    multiProjectGroupType === "system"
                      ? ["project", "projectConnection", "projectAndProjectConnection"]
                      : ["projectConnection", "projectAndProjectConnection"]
                  }
                  emptyItem={t("page.community-groups.create-or-edit.posting-level.options.default")}
                  keySelector={(x) => x}
                  renderOption={(x) => {
                    switch (x) {
                      case "project":
                        return t("page.community-groups.create-or-edit.posting-level.options.project");
                      case "projectConnection":
                        return t("page.community-groups.create-or-edit.posting-level.options.connection");
                      case "projectAndProjectConnection":
                        return t("page.community-groups.create-or-edit.posting-level.options.both");
                    }
                  }}
                  rules={{
                    required: t("components.form.error.required", {
                      inputName: t("page.community-groups.create-or-edit.posting-level.label"),
                    }),
                  }}
                />
              </FormField>
              {user.isSuperAdmin && (
                <FormField
                  label={t("component.permission-section.title")}
                  description={t("component.permission-section.description", {
                    permissionTarget: t("common.entity.group", { count: 1 }).toLowerCase(),
                  })}
                >
                  <PermissionSelector
                    defaultPermissions={permissions}
                    fields={permissionFields}
                    onSave={(newPermissions) => formMethods.setValue("permissions", newPermissions)}
                  />
                </FormField>
              )}
            </Form.Body>
            <Form.Footer
              renderPrimaryAction={() => (
                <Button type="submit" isLoading={isSubmitting}>
                  {translations.actionFinish}
                </Button>
              )}
            />
          </Form.Container>
        )}
        {!isMultiProjectGroup && (
          <WithAppPreviewLayout preview={<Preview />}>
            <Form.Container>
              <Form.Body>
                <Notice
                  icon={<Translate01Icon />}
                  variant="special"
                  message={
                    props.isEditMode
                      ? t("common.label.you-are-editing-in-original-content-language")
                      : t("common.label.your-input-will-be-auto-translated")
                  }
                />
                <FormField label={t(translations.fieldName)} actions={flagIcon} required>
                  <FormInput<CreateOrEditCommunityGroupFormValues>
                    data-testid="group-name"
                    name="name"
                    rules={{
                      validate: {
                        required: createRequiredStringRule(t, translations.fieldName),
                      },
                      maxLength: {
                        message: t("components.form.error.max-length", {
                          length: NAME_MAX_LENGTH,
                        }),
                        value: NAME_MAX_LENGTH,
                      },
                    }}
                    placeholder={translations.fieldNamePlaceholder}
                  />
                </FormField>
                <FormField label={t(translations.fieldDescription)} required actions={flagIcon}>
                  <FormTextArea<CreateOrEditCommunityGroupFormValues>
                    data-testid="group-description"
                    name="description"
                    rules={{
                      validate: {
                        required: createRequiredStringRule(t, translations.fieldDescription),
                      },
                    }}
                    placeholder={translations.fieldDescriptionPlaceholder}
                  />
                </FormField>
                <FormField label={translations.fieldIcon} description={translations.fieldIconDescription} required>
                  <FormGroupIconPicker<CreateOrEditCommunityGroupFormValues>
                    data-testid="group-icon"
                    name="icon"
                    placeholder={translations.fieldIconPlaceholder}
                    rules={{
                      required: t("components.form.error.required", {
                        inputName: translations.fieldIcon,
                      }),
                    }}
                  />
                </FormField>
                <FormField label={translations.fieldImage} description={translations.fieldImageDescription}>
                  <FormImageInput<CreateOrEditCommunityGroupFormValues, "image">
                    data-testid="group-image"
                    name="image"
                    rules={{
                      validate: {
                        size: createImageSizeRule({
                          t,
                        }),
                        type: createImageTypeRule({
                          t,
                        }),
                      },
                    }}
                  />
                </FormField>
                <FormField
                  label={t("common.entity.audience")}
                  description={t("component.audience-section.description", {
                    audienceTarget: t("common.entity.group", { count: 1 }).toLowerCase(),
                  })}
                >
                  <GroupAudienceSelector isEditMode={props.isEditMode} groupType={type} onSave={onSave} />
                </FormField>
                {user.isSuperAdmin && (
                  <FormField
                    label={t("component.permission-section.title")}
                    description={t("component.permission-section.description", {
                      permissionTarget: t("common.entity.group", { count: 1 }).toLowerCase(),
                    })}
                  >
                    <PermissionSelector
                      defaultPermissions={permissions}
                      fields={permissionFields}
                      onSave={(newPermissions) => formMethods.setValue("permissions", newPermissions)}
                    />
                  </FormField>
                )}
              </Form.Body>
              <Form.Footer
                renderPrimaryAction={() => (
                  <Button type="submit" isLoading={isSubmitting}>
                    {translations.actionFinish}
                  </Button>
                )}
              />
            </Form.Container>
          </WithAppPreviewLayout>
        )}
      </Form.Root>
    </DocumentPage>
  );
}
export interface CreateOrEditCommunityGroupFormValues {
  name: string;
  description: string;
  icon?: GroupIconType;
  audience: ConstraintListItemDto[];
  type: "interest" | "helpCategory" | "realEstate";
  image: FormImage[];
  permissions: FormPermissions<UserRoleCommunityGroupPermissionsDto>;
  isAllResidentsAdded: boolean;
  isRealEstate?: boolean;
  postingLevel?: "project" | "projectConnection" | "projectAndProjectConnection";
  enabled?: boolean;
}

function Preview() {
  const user = useSessionUser();
  const { name, description, icon, image } = useWatch<CreateOrEditCommunityGroupFormValues>();

  return (
    <Phone>
      <AppGroupDetailsView
        name={name?.slice(0, NAME_MAX_LENGTH) || ""}
        description={description || ""}
        icon={icon!}
        memberCount={12}
        image={image?.[0]?.url ?? user.project.backgroundImage?.url ?? bgPlaceholder}
        user={user}
      />
    </Phone>
  );
}
const NAME_MAX_LENGTH = 40;
